// Funciones de formato reutilizables para exportación
// Este archivo contiene todas las funciones de formato compartidas

// Formato de moneda
export const formatCurrency = (value, options = {}) => {
  const {
    symbol = '$',
    decimals = 2,
    thousandsSeparator = true,
    showSymbol = true
  } = options;

  if (value === null || value === undefined || isNaN(value)) {
    return showSymbol ? `${symbol}0.00` : '0.00';
  }

  const num = parseFloat(value);
  let formatted = num.toFixed(decimals);

  if (thousandsSeparator) {
    formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  return showSymbol ? `${symbol}${formatted}` : formatted;
};

// Formato de número
export const formatNumber = (value, options = {}) => {
  const {
    decimals = 2,
    thousandsSeparator = true
  } = options;

  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  const num = parseFloat(value);
  let formatted = decimals >= 0 ? num.toFixed(decimals) : num.toString();

  if (thousandsSeparator) {
    const parts = formatted.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    formatted = parts.join('.');
  }

  return formatted;
};

// Formato de porcentaje
export const formatPercentage = (value, options = {}) => {
  const {
    decimals = 2,
    multiply = false, // Si el valor ya está en porcentaje (ej: 16) o decimal (ej: 0.16)
    showSymbol = true
  } = options;

  if (value === null || value === undefined || isNaN(value)) {
    return showSymbol ? '0%' : '0';
  }

  const num = parseFloat(value);
  const percentage = multiply ? num * 100 : num;
  const formatted = percentage.toFixed(decimals);

  return showSymbol ? `${formatted}%` : formatted;
};

// Formato de fecha
export const formatDate = (date, format = 'DD/MM/YYYY') => {
  if (!date) return '';

  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();

  const monthNames = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];

  const monthNamesShort = [
    'ene', 'feb', 'mar', 'abr', 'may', 'jun',
    'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
  ];

  switch (format) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'DD-MM-YYYY':
      return `${day}-${month}-${year}`;
    case 'fechaCompleta':
      return `${day} de ${monthNames[d.getMonth()]} de ${year}`;
    case 'fechaCorta':
      return `${day}-${monthNamesShort[d.getMonth()]}-${year}`;
    default:
      return `${day}/${month}/${year}`;
  }
};

// Convertir número a texto (para totales)
export const numberToText = (number, options = {}) => {
  const {
    currency = 'PESOS',
    cents = 'CENTAVOS',
    showCents = true
  } = options;

  const units = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
  const tens = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
  const teens = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
  const hundreds = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

  const convertGroup = (n) => {
    if (n === 0) return '';
    if (n === 100) return 'CIEN';

    let result = '';

    // Centenas
    const h = Math.floor(n / 100);
    if (h > 0) {
      result += hundreds[h] + ' ';
      n %= 100;
    }

    // Decenas y unidades
    if (n >= 10 && n <= 19) {
      result += teens[n - 10];
    } else {
      const t = Math.floor(n / 10);
      const u = n % 10;

      if (t > 0) {
        if (t === 2 && u > 0) {
          result += 'VEINTI' + units[u].toLowerCase();
        } else {
          result += tens[t];
          if (u > 0) {
            result += ' Y ' + units[u];
          }
        }
      } else if (u > 0) {
        result += units[u];
      }
    }

    return result.trim();
  };

  const num = Math.floor(Math.abs(number));
  const decimal = Math.round((Math.abs(number) - num) * 100);

  if (num === 0) {
    const centsText = showCents && decimal > 0 ? ` CON ${decimal}/100 ${cents}` : '';
    return `CERO ${currency}${centsText}`;
  }

  let result = '';

  // Millones
  const millions = Math.floor(num / 1000000);
  if (millions > 0) {
    if (millions === 1) {
      result += 'UN MILLÓN ';
    } else {
      result += convertGroup(millions) + ' MILLONES ';
    }
  }

  // Miles
  const thousands = Math.floor((num % 1000000) / 1000);
  if (thousands > 0) {
    if (thousands === 1) {
      result += 'MIL ';
    } else {
      result += convertGroup(thousands) + ' MIL ';
    }
  }

  // Cientos
  const remainder = num % 1000;
  if (remainder > 0) {
    result += convertGroup(remainder);
  }

  result = result.trim() + ' ' + currency;

  if (showCents && decimal > 0) {
    result += ` CON ${decimal}/100 ${cents}`;
  }

  return result;
};

// Truncar texto largo
export const truncateText = (text, maxLength = 50, suffix = '...') => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - suffix.length) + suffix;
};

// Formatear código de concepto
export const formatCode = (code, options = {}) => {
  const {
    prefix = '',
    suffix = '',
    padLength = 0,
    padChar = '0'
  } = options;

  if (!code) return '';

  let formatted = String(code);

  if (padLength > 0 && formatted.length < padLength) {
    formatted = formatted.padStart(padLength, padChar);
  }

  return `${prefix}${formatted}${suffix}`;
};

// Formatear unidad de medida
export const formatUnit = (unit) => {
  if (!unit) return 'PZA';

  const unitMap = {
    'pieza': 'PZA',
    'pza': 'PZA',
    'metro': 'M',
    'metros': 'M',
    'm': 'M',
    'metro cuadrado': 'M²',
    'metros cuadrados': 'M²',
    'm2': 'M²',
    'metro cubico': 'M³',
    'metros cubicos': 'M³',
    'm3': 'M³',
    'kilogramo': 'KG',
    'kilogramos': 'KG',
    'kg': 'KG',
    'litro': 'L',
    'litros': 'L',
    'l': 'L',
    'tonelada': 'TON',
    'toneladas': 'TON',
    'ton': 'TON',
    'hora': 'HR',
    'horas': 'HR',
    'hr': 'HR',
    'dia': 'DÍA',
    'dias': 'DÍAS',
    'lote': 'LOTE',
    'lt': 'LOTE',
    'global': 'GLOBAL',
    'glb': 'GLOBAL'
  };

  const normalized = unit.toLowerCase().trim();
  return unitMap[normalized] || unit.toUpperCase();
};

// Calcular totales del presupuesto
export const calculateTotals = (conceptos, porcentajes) => {
  const costoDirecto = conceptos.reduce((sum, c) => {
    const cantidad = parseFloat(c.cantidad) || 0;
    const precioUnitario = parseFloat(c.precio_unitario || c.pu) || 0;
    return sum + (cantidad * precioUnitario);
  }, 0);

  const indirectos = costoDirecto * ((porcentajes.indirectos || 0) / 100);
  const financiamiento = costoDirecto * ((porcentajes.financiamiento || 0) / 100);
  const utilidad = costoDirecto * ((porcentajes.utilidad || 0) / 100);
  const subtotal = costoDirecto + indirectos + financiamiento + utilidad;
  const iva = subtotal * ((porcentajes.iva || 16) / 100);
  const total = subtotal + iva;

  return {
    costoDirecto,
    indirectos,
    financiamiento,
    utilidad,
    subtotal,
    iva,
    total
  };
};

// Agrupar conceptos por partidas
export const groupByPartida = (conceptos) => {
  const grouped = {};

  conceptos.forEach(concepto => {
    const partida = concepto.partida || 'Sin Partida';
    if (!grouped[partida]) {
      grouped[partida] = {
        nombre: partida,
        conceptos: [],
        total: 0
      };
    }
    grouped[partida].conceptos.push(concepto);
    grouped[partida].total += (concepto.cantidad || 0) * (concepto.precio_unitario || 0);
  });

  return Object.values(grouped);
};

// Validar datos antes de exportar
export const validateExportData = (data) => {
  const errors = [];

  if (!data.presupuesto) {
    errors.push('No hay datos del presupuesto');
  } else {
    if (!data.presupuesto.nombre_obra) {
      errors.push('Falta el nombre de la obra');
    }
    if (!data.presupuesto.cliente) {
      errors.push('Falta el nombre del cliente');
    }
  }

  if (!data.conceptos || data.conceptos.length === 0) {
    errors.push('No hay conceptos en el presupuesto');
  } else {
    data.conceptos.forEach((concepto, index) => {
      if (!concepto.descripcion) {
        errors.push(`El concepto ${index + 1} no tiene descripción`);
      }
      if (!concepto.cantidad || concepto.cantidad <= 0) {
        errors.push(`El concepto ${index + 1} no tiene cantidad válida`);
      }
      if (!concepto.precio_unitario || concepto.precio_unitario < 0) {
        errors.push(`El concepto ${index + 1} no tiene precio unitario válido`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Preparar datos para exportación
export const prepareExportData = (presupuesto, conceptos, porcentajes) => {
  // Calcular totales
  const totales = calculateTotals(conceptos, porcentajes);

  // Preparar conceptos con importes calculados
  const conceptosProcesados = conceptos.map((concepto, index) => ({
    ...concepto,
    item: index + 1,
    cantidad: parseFloat(concepto.cantidad) || 0,
    precio_unitario: parseFloat(concepto.precio_unitario || concepto.pu) || 0,
    importe: (parseFloat(concepto.cantidad) || 0) * (parseFloat(concepto.precio_unitario || concepto.pu) || 0),
    unidad: formatUnit(concepto.unidad)
  }));

  // Preparar datos del presupuesto
  const presupuestoProcesado = {
    ...presupuesto,
    fecha_elaboracion: presupuesto.fecha_elaboracion || new Date(),
    folio: presupuesto.codigo || presupuesto.folio || 'SN',
    vigencia: presupuesto.vigencia || '30 días naturales'
  };

  return {
    presupuesto: presupuestoProcesado,
    conceptos: conceptosProcesados,
    porcentajes,
    totales,
    totalEnTexto: numberToText(totales.total)
  };
};

// Aplicar estilos a celdas de Excel
export const applyExcelStyles = (ws, template) => {
  if (!ws || !template || !template.estilos) return;

  // Aquí se aplicarían los estilos usando la biblioteca xlsx-style o similar
  // Por ahora retornamos la hoja sin modificar
  return ws;
};

// Generar nombre de archivo
export const generateFileName = (base, options = {}) => {
  const {
    incluirFecha = true,
    incluirFolio = false,
    folio = '',
    extension = ''
  } = options;

  let fileName = base;

  if (incluirFolio && folio) {
    fileName += `_${folio}`;
  }

  if (incluirFecha) {
    const fecha = new Date();
    const fechaStr = `${fecha.getFullYear()}${String(fecha.getMonth() + 1).padStart(2, '0')}${String(fecha.getDate()).padStart(2, '0')}`;
    fileName += `_${fechaStr}`;
  }

  if (extension && !fileName.endsWith(extension)) {
    fileName += extension;
  }

  return fileName;
};

export default {
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatDate,
  numberToText,
  truncateText,
  formatCode,
  formatUnit,
  calculateTotals,
  groupByPartida,
  validateExportData,
  prepareExportData,
  applyExcelStyles,
  generateFileName
};